
import { dateToMD, formatMinutesToHours } from "../../../utils/date";

function truthyDateMs(v) {
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : null;
}

export function groupTimecardsForPayroll(items = []) {
    const list = Array.isArray(items) ? items : [];
    const map = new Map();

    for (let i = 0; i < list.length; i++) {
        const tc = list[i] || {};
        const staffUuid = tc.staffUuid;
        const payPeriodUuid = tc.payPeriodUuid;

        // hard skip invalid keys (prevents "::" collisions)
        if (!staffUuid || !payPeriodUuid) continue;

        const key = `${staffUuid}::${payPeriodUuid}`;

        if (!map.has(key)) {
            map.set(key, {
                staffUuid,
                payPeriodUuid,
                displayName: tc.displayName ?? null,

                minutesWorked: 0,

                // group-level computed later:
                lockedAt: null,
                approvedAt: null,
                payrollCheckUuid: null,

                // raw timecards
                timecards: [],
                timecardUuids: [],
            });
        }

        const row = map.get(key);

        if (!row.displayName && tc.displayName) row.displayName = tc.displayName;

        row.timecards.push(tc);

        if (tc.uuid) row.timecardUuids.push(tc.uuid);

        const mw = Number(tc.minutesWorked);
        if (Number.isFinite(mw)) row.minutesWorked += mw;
    }

    const grouped = Array.from(map.values());

    for (let i = 0; i < grouped.length; i++) {
        const g = grouped[i];

        // sort once
        g.timecards.sort((a, b) => {
            const ad = truthyDateMs(a?.startsOn) ?? 0;
            const bd = truthyDateMs(b?.startsOn) ?? 0;
            return ad - bd;
        });

        // segments
        const starts = g.timecards
            .map((tc) => `${dateToMD(tc.startsOn)} ${formatMinutesToHours(tc.minutesWorked) || "0:00"}`)
            .filter(Boolean);

        g.segments = starts.join(", ");
        g.totalHours = formatMinutesToHours(g.minutesWorked) || "0:00";

        // de-dupe UUIDs (prevents double actions)
        g.timecardUuids = Array.from(new Set(g.timecardUuids));

        // --- group-level status computation ---
        const tcs = g.timecards;

        const allLocked = tcs.length > 0 && tcs.every((tc) => !!tc.lockedAt);
        const allApproved = tcs.length > 0 && tcs.every((tc) => !!tc.approvedAt);

        // choose a consistent representative timestamp
        // (max makes sense for “Locked {date}”)
        g.lockedAt = allLocked
            ? new Date(Math.max(...tcs.map((tc) => truthyDateMs(tc.lockedAt) ?? 0))).toISOString()
            : null;

        g.approvedAt = allApproved
            ? new Date(Math.max(...tcs.map((tc) => truthyDateMs(tc.approvedAt) ?? 0))).toISOString()
            : null;

        // payrollCheckUuid only if consistent across all timecards
        const checkSet = new Set(tcs.map((tc) => tc.payrollCheckUuid).filter(Boolean));
        g.payrollCheckUuid = checkSet.size === 1 ? Array.from(checkSet)[0] : null;

        // Optional: expose mixed state if you want to show warning badge
        g.hasMixedPayrollCheck = checkSet.size > 1;

        // --- precomputed action booleans for UI ---
        const hasCheck = !!g.payrollCheckUuid || g.hasMixedPayrollCheck;

        g.actions = {
            canLock: !g.lockedAt && g.timecardUuids.length > 0,
            canUnlock: !!g.lockedAt && !g.approvedAt && !hasCheck,
            canApprove: !!g.lockedAt && !g.approvedAt && !hasCheck,
            canUnapprove: !!g.approvedAt && !hasCheck,
            canPay: !!g.approvedAt && !hasCheck,
            canUnpay: !!g.payrollCheckUuid && !g.hasMixedPayrollCheck,
        };

    }

    return grouped;
}